import { createContext, createElement as h, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Copy, MoreHorizontal, RefreshCw } from 'lucide-react';

const MenuContext = createContext(null);
const groups = ['object', 'page', 'product'];
const copy = {
  ja: { title: 'ページの操作', object: '選択した項目', page: '現在のページ', product: 'クイックアクセス', link: 'ページのリンクをコピー', refresh: 'ページを再読み込み', copied: 'リンクをコピーしました', failed: '操作に失敗しました。もう一度お試しください。', copyFailed: 'コピーできません。アドレスバーからリンクをコピーしてください。' },
  zh: { title: '页面操作', object: '当前对象', page: '当前页面', product: '快捷入口', link: '复制页面链接', refresh: '刷新页面', copied: '链接已复制', failed: '操作未完成，请重试', copyFailed: '无法复制，请从地址栏复制链接' },
  en: { title: 'Page actions', object: 'Selected item', page: 'Current page', product: 'Quick access', link: 'Copy page link', refresh: 'Reload page', copied: 'Link copied', failed: 'Action failed. Please try again.', copyFailed: 'Unable to copy. Copy the link from the address bar.' },
};

export function shouldUseNativeContextMenu(target, { shiftKey = false } = {}) {
  if (shiftKey || !target) return true;
  if (target.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="textbox"], [data-context-menu-skip], [role="menu"], video, audio')) return true;
  return Boolean(window.getSelection()?.toString().trim());
}

export function normalizeContextMenuItems(items) {
  const seen = new Set();
  return groups.flatMap(group => items.filter(item => {
    if (item.hidden || seen.has(item.id) || (item.group ?? 'page') !== group) return false;
    seen.add(item.id);
    return true;
  }));
}

export async function copyContextLink(value) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return;
    }
  } catch { /* HTTP TEST and browsers denying clipboard access use the selection fallback. */ }
  const previous = document.activeElement;
  const input = document.createElement('textarea');
  input.value = value;
  input.setAttribute('aria-hidden', 'true');
  input.style.cssText = 'position:fixed;top:0;left:-9999px;opacity:0';
  (document.querySelector('[role="dialog"][aria-modal="true"]') ?? document.body).append(input);
  input.select();
  try {
    if (!document.execCommand('copy')) throw new Error('clipboard-unavailable');
  } finally {
    input.remove();
    if (previous instanceof HTMLElement && previous.isConnected) previous.focus({ preventScroll: true });
  }
}

export function ContextMenuView({ x, y, items, onClose, onAction, locale = 'zh', title, portalRoot, autoFocusItem = false }) {
  const labels = copy[locale.startsWith('ja') ? 'ja' : locale.startsWith('en') ? 'en' : 'zh'];
  const ref = useRef(null);
  const [position, setPosition] = useState({ left: x, top: y });
  const ordered = useMemo(() => normalizeContextMenuItems(items), [items]);
  const viewportWidth = Math.min(window.visualViewport?.width ?? window.innerWidth, document.documentElement.clientWidth || window.innerWidth, document.documentElement.getBoundingClientRect().width || window.innerWidth);
  const viewportHeight = Math.min(window.visualViewport?.height ?? window.innerHeight, document.documentElement.clientHeight || window.innerHeight);
  useLayoutEffect(() => {
    const menu = ref.current;
    if (!menu) return;
    const rect = menu.getBoundingClientRect();
    const viewport = window.visualViewport;
    const left = viewport?.offsetLeft ?? 0, top = viewport?.offsetTop ?? 0;
    // Account for scrollbar gutters and transformed dialog portal containers.
    const originX = rect.left - position.left, originY = rect.top - position.top;
    setPosition({ left: Math.max(left + 8, Math.min(x, left + viewportWidth - rect.width - 8)) - originX, top: Math.max(top + 8, Math.min(y, top + viewportHeight - rect.height - 8)) - originY });
    // Pointer opening starts on the menu itself; keyboard opening starts on an item.
    (autoFocusItem ? menu.querySelector('button:not(:disabled)') ?? menu : menu).focus({ preventScroll: true });
  }, [x, y, items, autoFocusItem]);
  useEffect(() => {
    const outside = event => { if (!ref.current?.contains(event.target)) onClose('outside'); };
    const scroll = event => { if (!ref.current?.contains(event.target)) onClose('scroll'); };
    const close = () => onClose('viewport');
    document.addEventListener('pointerdown', outside);
    document.addEventListener('scroll', scroll, true);
    window.addEventListener('resize', close);
    window.addEventListener('blur', close);
    window.visualViewport?.addEventListener('resize', close);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('scroll', scroll, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('blur', close);
      window.visualViewport?.removeEventListener('resize', close);
    };
  }, [onClose]);
  const onKeyDown = event => {
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); onClose('escape'); return; }
    if (event.key === 'Tab') { onClose('tab'); return; }
    const buttons = Array.from(ref.current?.querySelectorAll('button:not(:disabled)') ?? []);
    if (!buttons.length) return;
    const index = buttons.indexOf(document.activeElement);
    let next;
    if (event.key === 'ArrowDown') next = (index + 1) % buttons.length;
    if (event.key === 'ArrowUp') next = index < 0 ? buttons.length - 1 : (index - 1 + buttons.length) % buttons.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = buttons.length - 1;
    if (next !== undefined) { event.preventDefault(); event.stopPropagation(); buttons[next].focus(); }
  };
  return createPortal(h('div', { ref, role: 'menu', tabIndex: -1, 'aria-label': title ?? labels.title, className: 'rrui-context-menu', style: { ...position, maxWidth: Math.max(0, viewportWidth - 16), maxHeight: Math.max(0, viewportHeight - 16) }, 'data-context-menu-skip': '', onKeyDown, onContextMenu: event => event.preventDefault() },
    groups.map(group => {
      const entries = ordered.filter(item => (item.group ?? 'page') === group);
      return entries.length ? h('div', { key: group, role: 'group', 'aria-label': labels[group], className: 'rrui-context-menu__group' },
        h('div', { className: 'rrui-context-menu__label', 'aria-hidden': true }, labels[group]),
        entries.map(item => h('button', {
          key: item.id, type: 'button', role: 'menuitem', tabIndex: -1, disabled: item.disabled,
          className: `rrui-context-menu__item${item.danger ? ' rrui-context-menu__item--danger' : ''}${item.divider ? ' rrui-context-menu__item--separated' : ''}`,
          onClick: () => onAction(item),
        }, item.icon ? h(item.icon, { size: 17, strokeWidth: 1.6, 'aria-hidden': true }) : h('span', { className: 'rrui-context-menu__icon', 'aria-hidden': true }),
        h('span', { className: 'rrui-context-menu__text' }, item.label), item.shortcut ? h('span', { className: 'rrui-context-menu__shortcut', 'aria-hidden': true }, item.shortcut) : null)),
      ) : null;
    }),
  ), portalRoot ?? document.body);
}

export function ContextMenuProvider({ children, getItems, routeKey, locale = 'zh' }) {
  const [menu, setMenu] = useState(null);
  const [notice, setNotice] = useState('');
  const restore = useRef(null);
  const labels = copy[locale.startsWith('ja') ? 'ja' : locale.startsWith('en') ? 'en' : 'zh'];
  const resolver = useRef(getItems);
  resolver.current = getItems;
  const close = useCallback(reason => {
    setMenu(null);
    if (['escape', 'action', 'tab'].includes(reason) && restore.current?.isConnected) restore.current.focus({ preventScroll: true });
  }, []);
  useEffect(() => { setMenu(null); }, [routeKey, locale, getItems]);
  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(''), 4000);
    return () => window.clearTimeout(timer);
  }, [notice]);
  const openMenu = useCallback((event, objectItems = [], options = {}) => {
    const target = options.target ?? (event.target instanceof Element ? event.target : null);
    if (!options.fromTrigger && shouldUseNativeContextMenu(target, event)) { setMenu(null); return; }
    const rect = (options.anchor ?? target)?.getBoundingClientRect();
    const x = options.fromTrigger || (!event.clientX && !event.clientY) ? rect?.left ?? 8 : event.clientX;
    const y = options.fromTrigger || (!event.clientX && !event.clientY) ? rect?.bottom ?? 8 : event.clientY;
    const items = normalizeContextMenuItems([
      ...objectItems.map(item => ({ ...item, group: item.group ?? 'object' })),
      ...(resolver.current?.({ target, event: event.nativeEvent ?? event }) ?? []),
      { id: 'page-copy-link', group: 'page', label: labels.link, icon: Copy, onAction: async () => {
        try { await copyContextLink(window.location.href); setNotice(labels.copied); }
        catch { setNotice(labels.copyFailed); }
      } },
      { id: 'page-refresh', group: 'page', label: labels.refresh, icon: RefreshCw, onAction: () => window.location.reload() },
    ]);
    if (!items.length) return;
    event.preventDefault();
    restore.current = options.anchor ?? target?.closest('button, a[href], [tabindex]') ?? document.activeElement;
    setNotice('');
    const autoFocusItem = options.fromTrigger ? event.detail === 0 : event.clientX === 0 && event.clientY === 0;
    setMenu({ x, y, items, autoFocusItem, portalRoot: target?.closest('[role="dialog"], dialog') ?? document.body });
  }, [labels]);
  useEffect(() => {
    const context = event => {
      if (event.defaultPrevented) return;
      const target = event.target instanceof Element ? event.target : null;
      // Preserve browser link/image actions except on explicitly registered product objects.
      if (target?.closest('a[href], img') && !target.closest('[data-context-menu]')) { setMenu(null); return; }
      openMenu(event);
    };
    const keyboard = event => {
      if (event.defaultPrevented || !(event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10'))) return;
      if (event.target?.closest?.('[role="menu"]')) return;
      const target = document.activeElement;
      if (shouldUseNativeContextMenu(target)) return;
      // Dispatch to the focused object's own handler before the document fallback.
      const request = new MouseEvent('contextmenu', { bubbles: true, cancelable: true });
      target?.dispatchEvent(request);
      if (request.defaultPrevented) event.preventDefault();
    };
    document.addEventListener('contextmenu', context);
    document.addEventListener('keydown', keyboard);
    return () => { document.removeEventListener('contextmenu', context); document.removeEventListener('keydown', keyboard); };
  }, [openMenu]);
  const action = async item => {
    if (item.disabled) return;
    close('action');
    try { await item.onAction(); } catch { setNotice(labels.failed); }
  };
  const value = useMemo(() => ({ openMenu, closeMenu: close, isOpen: Boolean(menu), locale }), [openMenu, close, menu, locale]);
  return h(MenuContext.Provider, { value }, children,
    menu ? h(ContextMenuView, { ...menu, locale, onClose: close, onAction: action }) : null,
    notice ? createPortal(h('div', { role: 'status', className: 'rrui-context-menu__notice' }, notice), document.body) : null,
  );
}

export function useContextMenu() { return useContext(MenuContext); }

export function ContextMenuTrigger({ items = [], label, className = '', title }) {
  const menu = useContextMenu();
  if (!menu) return null;
  const labels = copy[menu.locale.startsWith('ja') ? 'ja' : menu.locale.startsWith('en') ? 'en' : 'zh'];
  return h('button', {
    type: 'button', className: `rrui-context-menu-trigger ${className}`.trim(), 'aria-label': label ?? labels.title,
    title: title ?? label ?? labels.title, 'aria-haspopup': 'menu', 'data-context-menu-skip': '',
    onClick: event => { event.stopPropagation(); menu.openMenu(event, items, { fromTrigger: true, anchor: event.currentTarget }); },
  }, h(MoreHorizontal, { size: 20, strokeWidth: 1.6, 'aria-hidden': true }));
}
